/**
 * Heraldry: palette, divisions, charges and the (de)coding of a designed coat of arms into `House.heraldrySeed`.
 *
 * Random seeds live in 0..2_147_483_646 and are turned into arms by `crestFromSeed` (unchanged since launch, so
 * existing houses keep their arms). A designed crest is packed into the reserved range at and above
 * `CREST_SEED_BASE`; `decodeCrest` returns null for any seed outside it.
 */

export const TINCTURES = ['#7b2a26', '#2f4d73', '#2e5a3d', '#1b1814', '#6b3f73', '#9a5a22', '#4d5a5e'] as const;
export const METALS = ['#c9a64a', '#d8d2c0'] as const;
/** Every selectable colour: tinctures first, then metals. */
export const CREST_COLORS: readonly string[] = [...TINCTURES, ...METALS];

export const DIVISIONS = ['plain', 'pale', 'fess', 'bend', 'quarterly', 'chevron', 'saltire', 'chief'] as const;
export type Division = (typeof DIVISIONS)[number];
export const CHARGES = ['roundel', 'star', 'cross', 'crescent', 'lozenge', 'tower', 'trefoil'] as const;
export type Charge = (typeof CHARGES)[number];

export interface CrestSpec {
  division: Division;
  /** Field colour (one of CREST_COLORS). */
  field: string;
  /** Second colour, used by every division except 'plain'. */
  second: string;
  charge: Charge;
  chargeColor: string;
}

export const CREST_SEED_BASE = 2_147_483_647;
export const MAX_RANDOM_SEED = 2_147_483_646;
const NC = CREST_COLORS.length;
/** Number of distinct designed crests. */
export const CREST_COUNT = DIVISIONS.length * NC * NC * CHARGES.length * NC;

export function isMetal(color: string): boolean {
  return (METALS as readonly string[]).includes(color);
}

export function isCrestSpec(x: unknown): x is CrestSpec {
  if (!x || typeof x !== 'object') return false;
  const c = x as Record<string, unknown>;
  return (DIVISIONS as readonly unknown[]).includes(c.division)
    && (CHARGES as readonly unknown[]).includes(c.charge)
    && [c.field, c.second, c.chargeColor].every((v) => typeof v === 'string' && CREST_COLORS.includes(v));
}

export function encodeCrest(spec: CrestSpec): number {
  const di = DIVISIONS.indexOf(spec.division);
  const fi = CREST_COLORS.indexOf(spec.field);
  const si = CREST_COLORS.indexOf(spec.second);
  const ci = CHARGES.indexOf(spec.charge);
  const ki = CREST_COLORS.indexOf(spec.chargeColor);
  if ([di, fi, si, ci, ki].some((i) => i < 0)) throw new Error('Invalid crest');
  return CREST_SEED_BASE + (((di * NC + fi) * NC + si) * CHARGES.length + ci) * NC + ki;
}

export function decodeCrest(seed: number): CrestSpec | null {
  if (!Number.isInteger(seed) || seed < CREST_SEED_BASE || seed >= CREST_SEED_BASE + CREST_COUNT) return null;
  let n = seed - CREST_SEED_BASE;
  const ki = n % NC; n = Math.floor(n / NC);
  const ci = n % CHARGES.length; n = Math.floor(n / CHARGES.length);
  const si = n % NC; n = Math.floor(n / NC);
  const fi = n % NC; n = Math.floor(n / NC);
  return { division: DIVISIONS[n], field: CREST_COLORS[fi], second: CREST_COLORS[si], charge: CHARGES[ci], chargeColor: CREST_COLORS[ki] };
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The procedural arms of a random heraldry seed. Output is frozen: existing houses depend on it. */
export function crestFromSeed(seed: number): CrestSpec {
  const rnd = mulberry32(seed || 1);
  const pick = <T,>(a: readonly T[]): T => a[Math.floor(rnd() * a.length) % a.length];
  const division = pick(DIVISIONS);
  const useMetalField = rnd() < 0.35;
  const field: string = useMetalField ? pick(METALS) : pick(TINCTURES);
  let second: string = rnd() < 0.5 ? pick(METALS) : pick(TINCTURES);
  if (second === field) second = useMetalField ? TINCTURES[0] : METALS[0];
  const fieldIsMetal = isMetal(field);
  const chargeColor: string = fieldIsMetal ? pick(TINCTURES.filter((t) => t !== second)) : pick(METALS);
  const charge = pick(CHARGES);
  return { division, field, second, charge, chargeColor };
}

/** Arms to draw for any heraldry seed: designed if it is in the reserved range, else the procedural ones. */
export function crestForSeed(seed: number): CrestSpec {
  return decodeCrest(seed) ?? crestFromSeed(seed);
}

/** Random arms that follow the rule of tincture (metal never on metal, colour never on colour). */
export function randomCrest(rnd: () => number = Math.random): CrestSpec {
  const pick = <T,>(a: readonly T[]): T => a[Math.floor(rnd() * a.length)];
  const fieldMetal = rnd() < 0.35;
  const field = fieldMetal ? pick(METALS) : pick(TINCTURES);
  const second = fieldMetal ? pick(TINCTURES) : pick(METALS);
  const chargeColor = fieldMetal ? pick(TINCTURES.filter((t) => t !== second)) : pick(METALS.filter((m) => m !== second));
  return { division: pick(DIVISIONS), field, second, charge: pick(CHARGES), chargeColor: chargeColor ?? METALS[0] };
}
